import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { opsQuery } from 'store/query';
import { opsApiInstance } from 'api/instance';

import {
  PayrollList,
  PayrollQueue,
  PayrollHistory,
  PayrollSummary,
  PayrollItemsTab,
  PayrollOpenShow,
  PayrollItemsList,
  PayrollQueueArgs,
  PayrollItemsArgs,
  PayrollRangeArgs,
  PayrollExportArgs,
  PayrollHistoryArgs,
  PayrollTabStatsMap,
  PayrollEmployeeRow,
  PayrollOpenShowsArgs,
  PayrollEmployeesArgs,
  PaidOutsideClixResult,
} from './types';

export * from './types';

export const payrollTagTypes = ['Payroll'];

// Never send the page to Not Found if Ops lacks a route (an older Ops build
// during a deploy): the page shows its own error state instead.
const headers = { 'allow-not-found': 'true' };

/** `path?a=1&b=2`, keeping 0 and dropping only undefined / null / ''. */
export const withParams = (
  path: string,
  params: Record<string, string | number | null | undefined>
) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.append(key, String(value));
    }
  });
  const text = search.toString();
  return text ? `${path}?${text}` : path;
};

// PAYROLL DASHBOARD (plan §4.3, contract v1): read-only GETs on Ops, read
// live from the payout tables (no copies since 27 Oct 2026). Writes stay on
// today's Main endpoints.
export const payrollApi = createApi({
  baseQuery: opsQuery,
  reducerPath: 'payrollApi',
  tagTypes: payrollTagTypes,
  endpoints: (build) => ({
    // Any signed-in user (every Pay dialog): a light Ops read of the rental
    // vendors' Direct Billing method ids (QuickBooks role rows), never
    // gated by the rental lane's switch.
    payrollHiddenPayoutMethods: build.query<
      { paymentMethodIds: number[] },
      void
    >({
      query: () => ({
        headers,
        method: 'get',
        url: URL.PAYROLL_HIDDEN_PAYOUT_METHODS,
      }),
    }),
    payrollSummary: build.query<PayrollSummary, PayrollRangeArgs>({
      providesTags: payrollTagTypes,
      query: ({ to, from }) => ({
        headers,
        method: 'get',
        url: withParams(URL.PAYROLL_SUMMARY, { to, from }),
      }),
    }),
    payrollOpenShows: build.query<
      PayrollList<PayrollOpenShow>,
      PayrollOpenShowsArgs
    >({
      providesTags: payrollTagTypes,
      query: ({ q, limit, offset }) => ({
        headers,
        method: 'get',
        url: withParams(URL.PAYROLL_OPEN_SHOWS, { q, limit, offset }),
      }),
    }),
    payrollTabStats: build.query<
      PayrollTabStatsMap[PayrollItemsTab],
      PayrollRangeArgs & { tab: PayrollItemsTab }
    >({
      providesTags: payrollTagTypes,
      query: ({ to, tab, from }) => ({
        headers,
        method: 'get',
        url: withParams(URL.PAYROLL_TAB_STATS, { to, tab, from }),
      }),
    }),
    payrollQueue: build.query<PayrollQueue, PayrollQueueArgs>({
      providesTags: payrollTagTypes,
      query: ({ q, to, from, limit, offset, eventId, paidOutsideClix }) => ({
        headers,
        method: 'get',
        url: withParams(URL.PAYROLL_QUEUE, {
          q,
          to,
          from,
          limit,
          offset,
          eventId,
          paidOutsideClix,
        }),
      }),
    }),
    payrollHistory: build.query<PayrollHistory, PayrollHistoryArgs>({
      providesTags: payrollTagTypes,
      query: ({ q, to, from, limit, method, offset, eventId, employeeId }) => ({
        headers,
        method: 'get',
        url: withParams(URL.PAYROLL_HISTORY, {
          q,
          to,
          from,
          limit,
          offset,
          method,
          eventId,
          employeeId,
        }),
      }),
    }),
    payrollItems: build.query<PayrollItemsList, PayrollItemsArgs>({
      providesTags: payrollTagTypes,
      query: ({
        q,
        to,
        tab,
        from,
        limit,
        offset,
        status,
        eventId,
        employeeId,
      }) => ({
        headers,
        method: 'get',
        url: withParams(URL.PAYROLL_ITEMS, {
          q,
          to,
          tab,
          from,
          limit,
          offset,
          status,
          eventId,
          employeeId,
        }),
      }),
    }),
    payrollEmployees: build.query<
      PayrollList<PayrollEmployeeRow>,
      PayrollEmployeesArgs
    >({
      providesTags: payrollTagTypes,
      query: ({
        q,
        to,
        from,
        limit,
        offset,
        status,
        eventId,
        paidOutsideClix,
      }) => ({
        headers,
        method: 'get',
        url: withParams(URL.PAYROLL_EMPLOYEES, {
          q,
          to,
          from,
          limit,
          offset,
          status,
          eventId,
          paidOutsideClix,
        }),
      }),
    }),
  }),
});

export const {
  usePayrollItemsQuery,
  usePayrollQueueQuery,
  usePayrollHistoryQuery,
  usePayrollSummaryQuery,
  usePayrollTabStatsQuery,
  usePayrollOpenShowsQuery,
  usePayrollEmployeesQuery,
  usePayrollHiddenPayoutMethodsQuery,
} = payrollApi;

/**
 * The open list as CSV, built on Ops (≤ 13 months, ≤ 5,000 rows): its tab,
 * its search and filters, and the Overview's ticked rows. Ops filters one
 * `employeeId`; `employeeIds` carries a larger selection.
 */
export const fetchPayrollCsv = async ({
  q,
  to,
  tab,
  from,
  status,
  method,
  eventId,
  allDates,
  employeeIds,
  paidOutsideClix,
}: PayrollExportArgs) => {
  const response = await opsApiInstance.get<Blob>(
    withParams(URL.PAYROLL_EXPORT_CSV, {
      q,
      tab,
      status,
      method,
      eventId,
      paidOutsideClix,
      to: allDates ? undefined : to,
      from: allDates ? undefined : from,
      employeeId: employeeIds?.length === 1 ? employeeIds[0] : undefined,
      employeeIds:
        employeeIds && employeeIds.length > 1
          ? employeeIds.join(',')
          : undefined,
    }),
    { headers, responseType: 'blob' }
  );
  return response.data;
};

/** The employee profile's "Reconciled by accountant" switch (owner 28 Oct):
 * the saved value for one employee. Company Owner only (Ops answers 403 to
 * anyone else; 404 from an Ops without the read). */
export const getPaidOutsideClix = async (employeeId: number) => {
  const response = await opsApiInstance.get<
    Pick<PaidOutsideClixResult, 'employeeId' | 'paidOutsideClix'>
  >(URL.PAYROLL_PAID_BY_ACCOUNTANT.replace(':id', String(employeeId)), {
    headers,
  });
  return response.data;
};

/** "Reconciled by accountant" for one employee (owner 27 Oct): Company Owner
 * only (Ops answers 403 to anyone else and writes nothing). The caller
 * re-reads the payroll lists after it. */
export const putPaidOutsideClix = async ({
  employeeId,
  paidOutsideClix,
}: {
  employeeId: number;
  paidOutsideClix: boolean;
}) => {
  const response = await opsApiInstance.put<PaidOutsideClixResult>(
    URL.PAYROLL_PAID_BY_ACCOUNTANT.replace(':id', String(employeeId)),
    { paidOutsideClix },
    { headers }
  );
  return response.data;
};
